<template>
  <div class="orders-toolbar d-flex mb-5">
    <ToolbarContentSelector
      v-if="showOrdersGrid"
      class="orders-toolbar__toolbar-selector"
      :options="gridSelectorOptions"
      @value-change="selectGridContent"
    />

    <ToolbarSearchInput
      v-model="searchInput"
      :placeholder="t('Find an order')"
      class="orders-toolbar__toolbar-search"
    />
    <div class="orders-toolbar__new-order-btn">
      <Button
        v-if="showOrdersGrid && hasOrderNewPermission"
        @click="createNew"
      >
        {{ t("New Order") }}
      </Button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted } from "vue";
import ToolbarSearchInput from "@/components/common/ToolbarSearchInput.vue";
import ToolbarContentSelector from "@/components/common/ToolbarContentSelector.vue";
import Button from "@/components/input-components/Button.vue";
import { useRouter} from 'vue-router';
import { useOrdersStore } from "@/stores/orders";
import { usePhysiciansStore } from "@/stores/physicians";
import { usePermissionsService } from '@/composables/usePermissionsService';
import { useI18n } from 'vue-i18n'
import { useOrdersGridVisibility } from '@/composables/useOrdersGridVisibility';
import NotificationMessageService from "@/services/notificationmessage.service";

defineOptions({ name: "OrdersToolbar" });

const router = useRouter();
const ordersStore = useOrdersStore();
const physicianStore = usePhysiciansStore();
const permissions = usePermissionsService();
const { t } = useI18n()
const { showOrdersGrid } = useOrdersGridVisibility();

const searchInput = computed({
  get: () => ordersStore.searchInput ?? "",
  set: (value: string) => ordersStore.searchInput = value,
});

const physician = computed(() => physicianStore.physician);

const hasOrderNewPermission = computed(() => {
  return permissions.hasOrderCreatePermission || permissions.hasOrderCreateAllPermission;
});

const gridSelectorOptions = computed(() => {
  if (permissions.hasRoleCegatVerified || permissions.hasOrderReadAllPermission) {
    return ["My Orders", "Shared Orders"];
  }
  return ["My Orders"];
});

function selectGridContent(selection: 'My Orders' | 'Shared Orders') {
  if (gridSelectorOptions.value.includes(selection)) {
    ordersStore.setGridContentType(selection);
  }
}

function createNew() {
  if (permissions.hasOrderCreateAllPermission && !physician.value.userId) {
    NotificationMessageService.showError("orders.physician_selection_required");

    return;
  }

  ordersStore.resetOrderSupportingFiles();
  ordersStore.resetOrder();
  ordersStore.clearOrderPatient()
  router.push({ path: "/order/new" });
}

onMounted(() => {
  ordersStore.searchInput = "";
});
</script>
<style lang="scss">
  .orders-toolbar {
    @media #{map.get($display-breakpoints, 'sm-and-down')} {
      flex-direction: column;

      .orders-toolbar__toolbar-selector,
      .orders-toolbar__toolbar-search,
      .orders-toolbar__new-order-btn {
        margin-bottom: 1rem;
      }
    }

    @media #{map.get($display-breakpoints, 'md-and-up')} {
      .orders-toolbar__new-order-btn {
        margin-left: auto;

        button {
          margin-left: 0.5rem;
        }
      }
    }
  }
</style>
